import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import {
  validatePAN,
  validateGST,
  validateMobile,
  validateEmail,
  validateIFSC,
  validateAccountNumber,
} from "../../../../utils/validationUtils";
import {
  toDateInput,
  validateAgreementStartDate,
  validateAgreementEndDate,
} from "../../../../utils/dateUtils";
import { useDepartmentOptions } from "../../../../actions/fetchDepartmentListAction";
import { fetchPincodeDetailsAction } from "../../../../actions/pincodeAction";
import { updateEntityAction } from "../../../../actions/updateEntityAction";

const validateField = (fieldName, value, formData = {}) => {
  if (!value || (typeof value === "string" && value.trim() === "")) return null;
  switch (fieldName) {
    case "panNumber":
      return validatePAN(value) === true ? null : validatePAN(value);
    case "gstNumber":
      return validateGST(value) === true ? null : validateGST(value);
    case "mobileNumber":
      return validateMobile(value) === true ? null : validateMobile(value);
    case "emailAddress":
      return validateEmail(value) === true ? null : validateEmail(value);
    case "ifscCode":
      return validateIFSC(value) === true ? null : validateIFSC(value);
    case "accountNumber":
      return validateAccountNumber(value) === true
        ? null
        : validateAccountNumber(value);

    default:
      return null;
  }
};

const normalizeDepartmentValue = (value, options) => {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  if (!raw) return "";
  if (options.some((o) => o.value === raw)) return raw;
  const byName = options.find(
    (o) => o.label.toLowerCase() === raw.toLowerCase(),
  );
  return byName ? byName.value : "";
};

export const useEditDetails = (clientData, onSave, onClose) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const departmentOptions = useDepartmentOptions();

  // Populate form when clientData changes
  useEffect(() => {
    if (clientData?.entity) {
      const rawDept =
        clientData.entity.aaDepartmentFunctionId ??
        clientData.entity.aaDepartmentFunction;
      setFormData({
        ...clientData.entity,
        entityType: normalizeEntityType(clientData.entity.entityType),
        lobType: normalizeLOBType(clientData.entity.lobType),
        lobNationality: normalizeLOBNationality(
          clientData.entity.lobNationality,
        ),
        aaDepartmentFunction: normalizeDepartmentValue(
          rawDept,
          departmentOptions,
        ),
        agreementStartDate: toDateInput(clientData.entity.agreementStartDate),
        agreementEndDate: toDateInput(clientData.entity.agreementEndDate),
      });
    }
  }, [clientData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-normalize department when options finish loading
  useEffect(() => {
    if (departmentOptions.length <= 1) return;
    const rawDept =
      clientData?.entity?.aaDepartmentFunctionId ??
      clientData?.entity?.aaDepartmentFunction;
    if (rawDept == null) return;
    const normalized = normalizeDepartmentValue(rawDept, departmentOptions);
    setFormData((prev) => {
      if (normalized === prev.aaDepartmentFunction) return prev;
      return { ...prev, aaDepartmentFunction: normalized };
    });
  }, [departmentOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fill city & state when pinCode reaches 6 digits
  const pincodeRef = useRef(null);
  useEffect(() => {
    const pin = formData.pinCode
      ? String(formData.pinCode).replace(/\D/g, "")
      : "";
    if (pin.length !== 6) return;
    if (pincodeRef.current === pin) return;
    pincodeRef.current = pin;
    fetchPincodeDetailsAction(pin).then((result) => {
      if (result.success) {
        setFormData((prev) => ({
          ...prev,
          city: result.city,
          state: result.state,
        }));
      }
    });
  }, [formData.pinCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const normalizeEntityType = (value) => {
    if (!value) return "";
    const mapping = {
      proprietorship: "proprietorship",
      partnership: "partnership",
      llp: "llp",
      private_limited: "private_limited",
      public_limited: "public_limited",
      trust: "trust",
      society: "society",
      client: "",
      vendor: "",
    };
    return mapping[value.toLowerCase().trim()] ?? value;
  };

  const normalizeLOBType = (value) => {
    if (!value) return "";
    const mapping = {
      travel: "travel",
      company: "company",
      auto: "auto",
      gadget: "gadget",
      "non-travel": "company",
      nontravel: "company",
    };
    return mapping[value.toLowerCase().trim()] ?? value;
  };

  const normalizeLOBNationality = (value) => {
    if (!value) return "";
    const mapping = { domestic: "Domestic", international: "international" };
    return mapping[value.toLowerCase().trim()] ?? value;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    const uppercaseFields = [
      "panNumber",
      "companyPAN",
      "gstNumber",
      "ifscCode",
    ];
    if (uppercaseFields.includes(name) && typeof newValue === "string") {
      newValue = newValue.toUpperCase();
    }

    if (
      (name === "mobileNumber" ||
        name === "accountNumber" ||
        name === "pinCode") &&
      typeof newValue === "string"
    ) {
      newValue = newValue
        .replace(/\D/g, "")
        .slice(0, name === "pinCode" ? 6 : undefined);
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));

    if (type !== "checkbox") {
      const updatedForm = { ...formData, [name]: newValue };
      const error = validateField(name, newValue, updatedForm);
      setErrors((prev) => {
        const e = { ...prev };
        if (error) e[name] = error;
        else delete e[name];
        if (name === "agreementStartDate" || name === "agreementEndDate") {
          const endErr = validateAgreementEndDate(
            updatedForm.agreementEndDate,
            updatedForm.agreementStartDate,
          );
          const startErr = validateAgreementStartDate(
            updatedForm.agreementStartDate,
            updatedForm.agreementEndDate,
          );
          if (endErr) e.agreementEndDate = endErr;
          else delete e.agreementEndDate;
          if (startErr) e.agreementStartDate = startErr;
          else delete e.agreementStartDate;
        }
        return e;
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value, formData);
    setErrors((prev) => {
      const e = { ...prev };
      if (error) e[name] = error;
      else delete e[name];
      return e;
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const startErr = validateAgreementStartDate(
      formData.agreementStartDate,
      formData.agreementEndDate,
    );
    const endErr = validateAgreementEndDate(
      formData.agreementEndDate,
      formData.agreementStartDate,
    );
    if (startErr || endErr) {
      setErrors((prev) => ({
        ...prev,
        ...(startErr ? { agreementStartDate: startErr } : {}),
        ...(endErr ? { agreementEndDate: endErr } : {}),
      }));
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await updateEntityAction(formData.id, formData);
      if (result.success) {
        toast.success("Entity updated successfully!");
        onClose();
      } else {
        toast.error(result.message || "Failed to update entity");
      }
    } catch {
      toast.error("Failed to update entity");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({});
    setErrors({});
    onClose();
  };

  // All documents must have at least one uploaded file to enable Approve
  const allDocsUploaded = (() => {
    const docs = clientData?.documents || [];
    if (docs.length === 0) return false;
    return docs.every((doc) => doc.files?.some((f) => f.isUploaded));
  })();

  // Document status badge — null if uploaded (no badge), pending badge otherwise
  const getDocumentStatusBadge = (isUploaded) => {
    if (isUploaded) return null;
    return <span className="doc-status pending">⏳ Pending</span>;
  };

  const getFieldValidationState = (fieldName) => {
    const value = formData[fieldName];
    const error = errors[fieldName];
    const hasValue = value && value.toString().trim() !== "";
    const hasValidation = [
      "panNumber",
      "gstNumber",
      "mobileNumber",
      "emailAddress",
      "ifscCode",
      "accountNumber",
    ].includes(fieldName);
    return {
      error,
      isValid: hasValue && !error && hasValidation,
      className: error
        ? "error"
        : hasValue && !error && hasValidation
          ? "success"
          : "",
    };
  };

  return {
    formData,
    errors,
    isSubmitting,
    departmentOptions,
    handleChange,
    handleBlur,
    handleUpdate,
    handleClose,
    getDocumentStatusBadge,
    getFieldValidationState,
    allDocsUploaded,
  };
};
